import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { LiveMap } from "../components/LiveMap";
import { ErrorScreen, FooterNote, LoadingSkeleton, NotFoundScreen } from "../components/Shell";
import {
  DriverCard,
  FareCard,
  RiderCard,
  RouteCard,
  SafetyCard,
  StatusCard,
} from "../components/TripCards";
import { useLiveTrip } from "../hooks/useLiveTrip";
import { useI18n } from "../i18n";
import { hasDriverPhase, isLivePhase, phaseOf, phaseTitle, stopViews } from "../lib/trip";
import { fetchSharedRoute } from "../services/trip";
import type { LatLng } from "../types/trip";

const ROUTE_REFRESH_MS = 60_000;

function useSharedRoute(token: string | undefined, key: string, live: boolean): LatLng[] {
  const [route, setRoute] = useState<LatLng[]>([]);
  useEffect(() => {
    if (!token || !key) return;
    let cancelled = false;
    const load = async () => {
      const result = await fetchSharedRoute(token);
      if (!cancelled && result) {
        setRoute(result.points.map((p) => ({ lat: p.latitude, lng: p.longitude })));
      }
    };
    void load();
    if (!live) return () => {
      cancelled = true;
    };
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, ROUTE_REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [token, key, live]);
  return route;
}

export default function TripPage() {
  const { tripID: token } = useParams<{ tripID: string }>();
  const { t } = useI18n();
  const { state, trip, driver, etaMinutes, lastUpdate, connection, retry } = useLiveTrip(token);

  const phase = phaseOf(trip?.tripStatus);
  const live = isLivePhase(phase);
  const stops = useMemo(() => (trip ? stopViews(trip, phase) : []), [trip, phase]);
  const routeKey = trip ? `${trip.tripStatus}:${trip.finalTripRoute?.length ?? 0}` : "";
  const route = useSharedRoute(token, routeKey, live);

  useEffect(() => {
    document.title = trip ? `${t(phaseTitle[phase])} · ${t("appName")}` : t("appName");
  }, [trip, phase, t]);

  if (state === "loading") return <LoadingSkeleton />;
  if (state === "notFound") return <NotFoundScreen />;
  if (state === "error" || !trip) return <ErrorScreen onRetry={retry} />;

  const showDriver = hasDriverPhase(phase) || phase === "completed";
  const canCall = live;

  return (
    <main className="lg:grid lg:h-[calc(100dvh-3.5rem)] lg:grid-cols-[minmax(360px,420px)_1fr]">
      <div className="sticky top-14 z-0 h-[42dvh] min-h-[260px] lg:order-2 lg:h-full">
        <LiveMap trip={trip} phase={phase} stops={stops} driver={driver} route={route} />
      </div>

      <div className="relative z-10 -mt-4 rounded-t-3xl bg-neutral-50 px-4 pt-4 lg:order-1 lg:mt-0 lg:overflow-y-auto lg:rounded-none lg:pt-4">
        <div className="mx-auto max-w-xl space-y-3">
          <StatusCard
            trip={trip}
            phase={phase}
            etaMinutes={etaMinutes}
            connection={connection}
            lastUpdate={lastUpdate}
          />
          {showDriver && <DriverCard trip={trip} showCall={canCall} />}
          <RouteCard stops={stops} />
          <RiderCard trip={trip} showCall={canCall} />
          <FareCard trip={trip} phase={phase} />
          <SafetyCard trip={trip} live={live} />
        </div>
        <FooterNote />
      </div>
    </main>
  );
}
